import AuditSidebar from "./AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { doc, setDoc, getDoc } from "firebase/firestore";
import { firestore } from "./firebase.config";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

export default function CurrencySettings() {
  const { auditId } = useParams();
  const { formsData } = useAuditProgress(auditId);
  const [toast, setToast] = useState(null);

  const [baseCarbonPrice, setBaseCarbonPrice] = useState(76);
  const [usdRate, setUsdRate] = useState(1.17);
  const [gbpRate, setGbpRate] = useState(0.86);
  const [inrRate, setInrRate] = useState(102.4);
  const navigate = useNavigate();

  const textFieldStyle = {
    width: 250,
    mx: "auto",
    display: "block",

    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      fontSize: "20px",
      fontWeight: 600,

      "& fieldset": {
        borderColor: "#94A3B8",
        borderWidth: "2px",
      },

      "&:hover fieldset": {
        borderColor: "#2563EB",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2563EB",
        borderWidth: "2px",
      },
    },

    "& .MuiInputBase-input": {
      fontWeight: 700,
      color: "#1E293B",
    },

    "& .MuiOutlinedInput-notchedOutline legend": {
      maxWidth: "fit-content",
      padding: "0 4px",
    },

    "& .MuiInputLabel-root": {
      backgroundColor: "#fff",
      padding: "0 4px",
      fontWeight: 600,
      color: "#475569",
    },
  };
  const STYLE = `
  .fa-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    display: flex; align-items: center; gap: 8px;
    animation: slideIn 0.2s ease, fadeOut 0.3s ease 2.5s forwards;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  @keyframes slideIn {
    from { transform: translateX(60px); opacity: 0; }
    to { transform: none; opacity: 1; }
  }
  @keyframes fadeOut { to { opacity: 0; transform: translateX(20px); } }
  .fa-toast.error { background: #dc2626; }

  .fa-datepicker-wrap .MuiOutlinedInput-root {
    border-radius: 4px;
    background: #f8fafc;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
  }
  `;

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const loadSettings = async () => {
    try {
      const ref = doc(
        firestore,
        "auditReports",
        auditId,
        "settings",
        "currency",
      );

      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data();

        setBaseCarbonPrice(data.baseCarbonPrice ?? 76);
        setUsdRate(data.usdRate ?? 1.17);
        setGbpRate(data.gbpRate ?? 0.86);
        setInrRate(data.inrRate ?? 102.4);
      }
    } catch (error) {
      console.error("Error loading currency settings:", error);
    }
  };

  const handleSave = async () => {
    try {
      await setDoc(
        doc(firestore, "auditReports", auditId, "settings", "currency"),
        {
          baseCarbonPrice: Number(baseCarbonPrice),
          usdRate: Number(usdRate),
          gbpRate: Number(gbpRate),
          inrRate: Number(inrRate),
        },
        { merge: true },
      );

      showToast("Currency settings saved successfully.");

      setTimeout(() => {
        navigate(`/dashboard/${auditId}`);
      }, 2800);
    } catch (error) {
      console.error("Error saving currency settings:", error);
      showToast("Failed to save currency settings.", "error");
    }
  };

  useEffect(() => {
    if (auditId) {
      loadSettings();
    }
  }, [auditId]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", width: "100%" }}>
      <style>{STYLE}</style>
      <AuditSidebar displayAuditId={auditId} formsData={formsData} />

      <div style={{ flex: 1, overflow: "auto" }}>
        <div className="dashboard-container">
          <Paper sx={{ padding: 4 }}>
            <Typography variant="h4" gutterBottom>
              Currency Configuration
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 5,
                mt: 4,
              }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  border: "1px solid #E5E7EB",
                  borderRadius: 4,
                  minHeight: 350,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mb={5}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Carbon Base Rate
                  </Typography>

                  <Tooltip title="Base carbon rate available for the current quarter. Update this value if the official quarterly base rate changes.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    position: "relative",
                    width: 250,
                    mx: "auto",
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "14px",
                      px: 1,
                      backgroundColor: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#475569",
                      zIndex: 1,
                    }}
                  >
                    Base Carbon Price (EUR)
                  </Typography>

                  <TextField
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={baseCarbonPrice}
                    onChange={(e) => setBaseCarbonPrice(e.target.value)}
                    fullWidth
                    sx={textFieldStyle}
                  />
                </Box>
              </Paper>

              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  border: "1px solid #E5E7EB",
                  borderRadius: 4,
                  minHeight: 350,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mb={5}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Currency Conversion Rate
                  </Typography>

                  <Tooltip title="Exchange rates shown are indicative only. Please refer to authorized sources for official exchange rates.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    width: 250,
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "14px",
                      px: 1,
                      backgroundColor: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#475569",
                      zIndex: 1,
                    }}
                  >
                    EUR to USD
                  </Typography>

                  <TextField
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={usdRate}
                    onChange={(e) => setUsdRate(e.target.value)}
                    fullWidth
                    sx={textFieldStyle}
                  />
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    width: 250,
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "14px",
                      px: 1,
                      backgroundColor: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#475569",
                      zIndex: 1,
                    }}
                  >
                    EUR to GBP
                  </Typography>

                  <TextField
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={gbpRate}
                    onChange={(e) => setGbpRate(e.target.value)}
                    fullWidth
                    sx={textFieldStyle}
                  />
                </Box>
                <Box
                  sx={{
                    position: "relative",
                    width: 250,
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "14px",
                      px: 1,
                      backgroundColor: "#fff",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#475569",
                      zIndex: 1,
                    }}
                  >
                    EUR to INR
                  </Typography>

                  <TextField
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={inrRate}
                    onChange={(e) => setInrRate(e.target.value)}
                    fullWidth
                    sx={textFieldStyle}
                  />
                </Box>
              </Paper>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.2,
                  borderRadius: 3,
                  fontWeight: 700,
                }}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Box>
          </Paper>
        </div>
      </div>

      {toast && (
        <div className={`fa-toast ${toast.type === "error" ? "error" : ""}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
